import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  InputAdornment,
  IconButton,
  Paper,
} from '@mui/material'
import { Visibility, VisibilityOff, Lock, Email } from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { setCredentials } from '../store/slices/authSlice'
import { useLoginMutation } from '../store/services/authApi'
import { useAuthStore } from '../stores/authStore'
import { brandGradients } from '../theme'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [login, { isLoading }] = useLoginMutation()
  const setZustandUser = useAuthStore(state => state.setUser)
  const setZustandToken = (token: string) => useAuthStore.setState({ token, isAuthenticated: true })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      const result = await login({ email, password }).unwrap()

      dispatch(
        setCredentials({
          user: {
            id: result.user.id,
            email: result.user.email,
            role: result.user.role,
            name: result.user.full_name,
            permissions: result.user.permissions || {},
            is_superuser: result.user.is_superuser || false,
          },
          token: result.access_token,
        })
      )

      // Sync with Zustand Store for the new RBAC system
      setZustandUser({
        id: result.user.id,
        email: result.user.email,
        role: result.user.role,
        permissions: result.user.permissions || {},
        is_superuser: result.user.is_superuser || false,
      })
      setZustandToken(result.access_token)

      enqueueSnackbar('Welcome back!', { variant: 'success' })
      navigate('/admin/documents/sales-orders')
    } catch (error: any) {
      const message = error?.data?.detail || 'Invalid credentials. Please try again.'
      enqueueSnackbar(message, { variant: 'error' })
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: brandGradients.primary,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 80% 80%, rgba(244,67,54,0.15) 0%, transparent 50%)',
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          }}
        >
          {/* Header Section with Logo */}
          <Box
            sx={{
              background: brandGradients.primary,
              py: 5,
              px: 4,
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="Dhanshika Exports"
              sx={{
                height: 80,
                width: 'auto',
                mb: 2,
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))',
              }}
            />
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: 'white',
                textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                letterSpacing: '0.5px',
              }}
            >
              Dhanshika Exports
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.95)',
                mt: 1,
                fontWeight: 500,
              }}
            >
              B2B Admin Portal
            </Typography>
          </Box>

          {/* Login Form */}
          <CardContent sx={{ p: 5 }}>
            <Typography
              variant="h5"
              fontWeight={600}
              gutterBottom
              align="center"
              sx={{ mb: 1, color: 'text.primary' }}
            >
              Welcome Back
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 4 }}
            >
              Sign in to access your dashboard
            </Typography>

            <form onSubmit={handleSubmit}>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoComplete="email"
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                autoComplete="current-password"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 4,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isLoading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  background: brandGradients.primary,
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.4)',
                  '&:hover': {
                    background: brandGradients.primary,
                    boxShadow: '0 6px 16px rgba(33, 150, 243, 0.5)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {isLoading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </CardContent>

          {/* Footer */}
          <Box
            sx={{
              px: 5,
              pb: 4,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} Dhanshika Exports. All rights reserved.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  )
}

export default Login
